import React from "react";
import { DialogTrigger, Menu, MenuItem } from "react-aria-components";
import type { ExportType } from "../../../utils/chartExport";
import { Button } from "../../Button";
import { Icon } from "../../Icon";
import { Popover } from "../../Popover";
import { Tray } from "../../Tray";

interface LegendItem {
  category: string;
  fill: string;
}

export interface ChartBottomBarProps {
  items: LegendItem[];
  onExport: (type: ExportType) => void | Promise<void>;
  marginLeft?: number;
  marginRight?: number;
  isZoomed?: boolean;
  onResetZoom?: () => void;
}

const LegendItem: React.FC<{ item: LegendItem }> = ({ item }) => (
  <div className="flex items-center gap-2 shrink-0">
    <div className="h-3 w-3 shrink-0" style={{ backgroundColor: item.fill, borderRadius: "1px" }} />
    <span className="text-sm text-text-caption whitespace-nowrap">{item.category}</span>
  </div>
);

const CompactLegend: React.FC<{ items: LegendItem[] }> = ({ items }) => {
  if (items.length === 0) return null;

  return (
    <div className="flex items-center gap-3 flex-wrap">
      {items.map((item) => (
        <LegendItem key={item.category} item={item} />
      ))}
    </div>
  );
};

/**
 * ChartBottomBar
 *
 * Chart footer component with legend and export functionality.
 * Displays color-coded legend items and provides export options for CSV, SVG, and PNG formats.
 *
 * **Smart legend display:**
 * - Desktop (≥ 640px): Shows all legend items inline with wrapping
 * - Mobile (< 640px): Shows "Legend (N)" button that opens a tray with all items
 * - Legend and action buttons always stay on a single row for clean, consistent layout
 *
 * **Responsive behavior:**
 * - Mobile: No padding, legend in tray, button text hidden (icon-only)
 * - Desktop: Chart margin padding, inline legend, full button labels
 *
 * Respects chart margins to align legend with Y-axis and export button with X-axis end.
 */
export const ChartBottomBar: React.FC<ChartBottomBarProps> = ({
  items,
  onExport,
  marginLeft: _marginLeft = 0,
  marginRight: _marginRight = 0,
  isZoomed = false,
  onResetZoom,
}) => {
  const [isExporting, setIsExporting] = React.useState(false);
  const [exportingType, setExportingType] = React.useState<ExportType | null>(null);
  const [isLegendTrayOpen, setIsLegendTrayOpen] = React.useState(false);
  const [isExportTrayOpen, setIsExportTrayOpen] = React.useState(false);

  const handleExport = React.useCallback(
    async (type: ExportType) => {
      setIsExporting(true);
      setExportingType(type);
      setIsExportTrayOpen(false);

      try {
        await onExport(type);
      } catch (error) {
        console.error("Export failed:", error);
      } finally {
        setIsExporting(false);
        setExportingType(null);
      }
    },
    [onExport]
  );

  const handleDesktopExport = React.useCallback(
    async (key: React.Key) => {
      await handleExport(key as ExportType);
    },
    [handleExport]
  );

  return (
    <div
      className="mt-4 flex items-center justify-between gap-3 min-h-[32px]"
      style={{ marginLeft: _marginLeft, marginRight: _marginRight }}
    >
      {/* Desktop: Show inline legend */}
      <div className="hidden sm:block flex-1 min-w-0">
        <CompactLegend items={items} />
      </div>

      {/* Mobile: Show separate legend and export links */}
      <div className="sm:hidden flex items-center gap-3">
        {items.length > 0 && (
          <>
            <button
              onClick={() => setIsLegendTrayOpen(true)}
              className="text-sm text-text-muted hover:text-text-body transition-colors underline decoration-dotted underline-offset-4"
            >
              Legend
            </button>

            <Tray isOpen={isLegendTrayOpen} onClose={() => setIsLegendTrayOpen(false)} title="Legend">
              <div className="grid grid-cols-1 gap-3 p-4">
                {items.map((item) => (
                  <LegendItem key={item.category} item={item} />
                ))}
              </div>
            </Tray>
          </>
        )}
      </div>

      {/* Mobile: Export actions */}
      <div className="sm:hidden flex items-center gap-2 shrink-0">
        {isZoomed && onResetZoom && (
          <Button variant="secondary" size="sm" onPress={onResetZoom}>
            <Icon name="ArrowsOut" size={16} />
          </Button>
        )}

        <Button variant="secondary" size="sm" onPress={() => setIsExportTrayOpen(true)} isDisabled={isExporting}>
          <Icon name="Export" size={16} />
        </Button>

        <Tray
          isOpen={isExportTrayOpen}
          onClose={() => setIsExportTrayOpen(false)}
          title="Export Chart"
          contentPadding={false}
        >
          <div className="flex flex-col">
            <button
              onClick={() => handleExport("csv")}
              disabled={isExporting}
              className="flex items-center gap-3 px-6 py-4 text-left hover:bg-background-hover active:bg-background-muted transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Icon name="List" size={20} className="text-text-muted" />
              <span className="text-sm text-text-body">
                {exportingType === "csv" ? "Exporting..." : "Export as CSV"}
              </span>
            </button>
            <button
              onClick={() => handleExport("svg")}
              disabled={isExporting}
              className="flex items-center gap-3 px-6 py-4 text-left hover:bg-background-hover active:bg-background-muted transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Icon name="Code" size={20} className="text-text-muted" />
              <span className="text-sm text-text-body">
                {exportingType === "svg" ? "Exporting..." : "Export as SVG"}
              </span>
            </button>
            <button
              onClick={() => handleExport("png")}
              disabled={isExporting}
              className="flex items-center gap-3 px-6 py-4 text-left hover:bg-background-hover active:bg-background-muted transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Icon name="DownloadSimple" size={20} className="text-text-muted" />
              <span className="text-sm text-text-body">
                {exportingType === "png" ? "Exporting..." : "Export as PNG"}
              </span>
            </button>
          </div>
        </Tray>
      </div>

      {/* Desktop: Actions - always on same row */}
      <div className="hidden sm:flex items-center gap-2 shrink-0">
        {isZoomed && onResetZoom && (
          <Button variant="secondary" size="sm" onPress={onResetZoom}>
            <Icon name="ArrowsOut" size={16} />
            <span className="hidden sm:inline">Reset Zoom</span>
          </Button>
        )}
        <DialogTrigger>
          <Button variant="secondary" size="sm" isDisabled={isExporting}>
            <Icon name="Export" size={16} />
            {isExporting ? "Exporting..." : "Export"}
          </Button>
          <Popover
            placement="top end"
            className="min-w-48 p-1 bg-white dark:bg-gray-800 rounded-lg shadow-xl ring-1 ring-gray-200 dark:ring-gray-700"
          >
            <Menu onAction={handleDesktopExport} className="outline-none">
              <MenuItem
                id="csv"
                className="flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700 rounded cursor-pointer outline-none"
                isDisabled={isExporting}
              >
                <span className="flex items-center gap-2">
                  <Icon name="List" size={14} />
                  {exportingType === "csv" ? "Exporting..." : "Export as CSV"}
                </span>
              </MenuItem>
              <MenuItem
                id="svg"
                className="flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700 rounded cursor-pointer outline-none"
                isDisabled={isExporting}
              >
                <span className="flex items-center gap-2">
                  <Icon name="Code" size={14} />
                  {exportingType === "svg" ? "Exporting..." : "Export as SVG"}
                </span>
              </MenuItem>
              <MenuItem
                id="png"
                className="flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700 rounded cursor-pointer outline-none"
                isDisabled={isExporting}
              >
                <span className="flex items-center gap-2">
                  <Icon name="DownloadSimple" size={14} />
                  {exportingType === "png" ? "Exporting..." : "Export as PNG"}
                </span>
              </MenuItem>
            </Menu>
          </Popover>
        </DialogTrigger>
      </div>
    </div>
  );
};
